import { isNilOrEmptyString } from '@tunein/web-utils';
import { mapKeys, mapValues, omit, omitBy } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { v4 as uuidv4 } from 'uuid';
import { defaultTargetingParams } from '../AdPlayer';
import { ONE_HOUR_IN_MILLISECONDS } from '../AdPlayer/constants';
import { AB_TEST_IDS, ADS_PERSONA } from '../constants';
import { appSessionDataProvider, localizationsProvider } from '../providers';
import { api } from '../singletons';
const GAM_ADS_URL = 'https://pubads.g.doubleclick.net/gampad/ads';
export function buildCommonCustomParams({ videoAdPlayed, isFirstInSession, }) {
    const { consentData, env, currentScreen, categoryId, firstVisitTimestampMs } = appSessionDataProvider;
    const { isTargetingEnabled } = consentData;
    const adsTargeting = isTargetingEnabled ? api.adsTargeting : omit(api.adsTargeting, ['ppid']);
    return {
        ...adsTargeting,
        lotamesegments: isTargetingEnabled ? consentData.audiences?.join(',') || '' : '',
        videoAdPlayed: videoAdPlayed.toString(),
        persona: api.getConfigSetting(ADS_PERSONA),
        abtest: api.getConfigSetting(AB_TEST_IDS),
        isLoggedIn: api.hasAuthTokens.toString(),
        screen: currentScreen,
        categoryId,
        language: localizationsProvider.locale,
        env,
        isFirstInSession: isFirstInSession.toString(),
        is_new_user: Boolean(firstVisitTimestampMs + ONE_HOUR_IN_MILLISECONDS > Date.now()).toString(),
    };
}
export function buildGoogleIMAAdTagUrl({ baseParams, isVideoAd, videoApsBids, }) {
    const url = new URL(GAM_ADS_URL);
    const filteredBaseParams = omitBy(baseParams, isNilOrEmptyString);
    const baseAdTagParams = {
        ...defaultTargetingParams,
        ...filteredBaseParams, // filtering early to avoid overriding defaults with empty values
        sid: uuidv4(),
        correlator: Date.now().toString(),
        sz: filteredBaseParams.sz || (isVideoAd ? '1x1|400x300|640x360|640x480' : '1x1'),
        ad_type: filteredBaseParams.ad_type || (isVideoAd ? 'video' : 'audio'),
        cust_params: {
            ...omitBy(baseParams.cust_params || {}, isNilOrEmptyString),
            videoEnabled: isVideoAd ? 'true' : 'false',
            audioEnabled: isVideoAd ? 'false' : 'true',
            ...videoApsBids?.adParams,
        },
    };
    const customParamsFiltered = omitBy(baseAdTagParams.cust_params || {}, isNilOrEmptyString);
    const customParamsTransformed = mapValues(customParamsFiltered, String);
    const baseParamsFiltered = omitBy(baseAdTagParams, isNilOrEmptyString);
    const baseParamsTransformed = mapValues(baseParamsFiltered, String);
    if (!isEmpty(customParamsTransformed)) {
        baseParamsTransformed.cust_params = new URLSearchParams(customParamsTransformed).toString();
    }
    else {
        delete baseParamsTransformed.cust_params;
    }
    if (baseParamsTransformed) {
        url.search = new URLSearchParams(baseParamsTransformed).toString();
    }
    return url.toString();
}
const ADSWIZZ_PREROLL_REQUEST_URL = 'https://tunein.deliveryengine.adswizz.com/vast/4.0/request/alias/VAST_Preroll';
const ADSWIZZ_MIDROLL_REQUEST_URL = 'https://tunein.deliveryengine.adswizz.com/vast/4.0/request/alias/VAST_Midroll';
export function buildAdsWizzAdTagUrl({ baseParams, customParams, companionSizes, adType, }) {
    const adswizzUrl = new URL(adType === 'preroll' ? ADSWIZZ_PREROLL_REQUEST_URL : ADSWIZZ_MIDROLL_REQUEST_URL);
    const baseAdTagParams = {
        ...baseParams,
        ...(companionSizes && {
            'aw_0_1st.companionAds': 'true',
            companion_zone_alias: companionSizes,
        }),
        'aw_0_1st.skey': '2112133398',
        idtype: 'adid',
        rdid: window.com_adswizz_synchro_getListenerId?.(),
    };
    if (customParams.lotamesegments) {
        customParams.lotamesegments = `;${customParams.lotamesegments.split(',').join(';')};`;
    }
    const adsWizzParameters = {
        ...baseAdTagParams,
        ...mapKeys(customParams, (_, key) => `aw_0_1st.${key}`),
    };
    if (adsWizzParameters) {
        const filteredParams = omitBy(adsWizzParameters, isNilOrEmptyString);
        const transformedParams = mapValues(filteredParams, String);
        adswizzUrl.search = new URLSearchParams(transformedParams).toString();
    }
    return adswizzUrl.toString();
}
